<template>
  <div class="confirmation-card" :style="{ 'background-color': confirmationSectionBackgroundColor }">
    <h2 class="confirmation-card__title" :style="{ color: confirmationSectionFontColor }">{{ confirmationTitle }}</h2>
    <p class="confirmation-card__description" :style="{ color: confirmationSectionFontColor }">{{ confirmationDescription }}</p>

    <div class="confirmation-card__form">
      <!-- Holder Card -->
      <person-card
        ref="holderCard"
        v-model="holderData"
        :disabled="isFormDisabled"
        :disable-cpf="true"
        :font-color="confirmationSectionFontColor"
        :background-color="confirmationSectionBackgroundColor"
      />

      <!-- Companion Cards -->
      <person-card
        v-for="(companion, index) in companionsData"
        :key="index"
        ref="companionCards"
        v-model="companionsData[index]"
        :title="`Acompanhante #${index + 1}`"
        :deletable="!isFormDisabled"
        :disabled="isFormDisabled"
        :font-color="confirmationSectionFontColor"
        :background-color="confirmationSectionBackgroundColor"
        @delete="removeCompanion(index)"
      />

      <v-btn v-if="!isFormDisabled && !isMaxCompanionsReached" outlined small class="confirmation-card__add-btn" @click="addCompanion">
        Adicionar acompanhante
      </v-btn>

      <!-- Confirm Button -->
      <v-btn
        block
        class="confirmation-card__confirm-btn"
        :disabled="isFormDisabled"
        :loading="loadingConfirm"
        :style="{
          'background-color': confirmationSectionButtonBackgroundColor,
          color: confirmationSectionButtonFontColor
        }"
        @click="handleConfirmClick"
      >
        {{ confirmButtonText }}
      </v-btn>

      <span class="confirmation-card__date-range" :style="{ color: confirmationSectionFontColor }">
        {{ confirmationDateRange }}
      </span>

      <!-- Terms Modal (hidden, opened programmatically) -->
      <terms-modal
        v-if="consumerTerms"
        ref="termsModal"
        :terms-url="consumerTerms"
        :button-background-color="confirmationSectionButtonBackgroundColor"
        :button-font-color="confirmationSectionButtonFontColor"
        @accept="confirmPresence"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import TermsModal from './TermsModal.vue'
import PersonInputCard from './PersonInputCard.vue'

export default {
  name: 'ConfirmationCard',
  components: {
    'terms-modal': TermsModal,
    'person-card': PersonInputCard
  },
  props: {
    initialGuest: {
      type: Object,
      default: () => ({
        documentNo: '',
        name: '',
        phone: ''
      })
    },
    initialCompanions: {
      type: Array,
      default: () => []
    },
    confirmed: {
      type: Boolean,
      default: false
    },
    maxCompanions: {
      type: Number,
      default: 0
    },
    confirmationTitle: {
      type: String,
      default: 'Confirmação de presença'
    },
    confirmationDescription: {
      type: String,
      default: ''
    },
    confirmationSectionBackgroundColor: {
      type: String,
      default: '#f5f5f5'
    },
    confirmationSectionFontColor: {
      type: String,
      default: '#000000'
    },
    confirmationSectionButtonBackgroundColor: {
      type: String,
      default: '#e0e0e0'
    },
    confirmationSectionButtonFontColor: {
      type: String,
      default: '#000000'
    },
    consumerTerms: {
      type: String,
      default: ''
    },
    confirmationStartDate: {
      type: String,
      default: null
    },
    confirmationEndDate: {
      type: String,
      default: null
    },
    isConfirmationLimitReached: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    holderData: {
      documentNo: '',
      name: '',
      phone: ''
    },
    companionsData: [],
    loadingConfirm: false
  }),
  computed: {
    ...mapGetters(['GET_STORE_EVENT_TOKEN']),
    isMaxCompanionsReached() {
      return this.companionsData.length >= this.maxCompanions
    },
    hasLessCompanionsThanMax() {
      return this.maxCompanions > 0 && this.companionsData.length < this.maxCompanions
    },
    isConfirmationActive() {
      if (!this.confirmationStartDate || !this.confirmationEndDate) {
        return true // If dates not set, allow confirmation
      }
      const now = this.$moment()
      const startDate = this.$moment(this.confirmationStartDate)
      const endDate = this.$moment(this.confirmationEndDate)
      return now.isBetween(startDate, endDate, null, '[]') // inclusive of both dates
    },
    confirmButtonText() {
      if (this.confirmed) {
        return 'Presença confirmada'
      }
      if (this.isConfirmationLimitReached) {
        return 'Vagas esgotadas'
      }
      if (this.isConfirmationActive) {
        return 'Confirmar presença'
      }
      if (!this.confirmationStartDate || !this.confirmationEndDate) {
        return 'Confirmar presença'
      }
      const now = this.$moment()
      const startDate = this.$moment(this.confirmationStartDate)
      const endDate = this.$moment(this.confirmationEndDate).seconds(59)

      if (now.isBefore(startDate)) {
        return 'CONFIRMAÇÕES AINDA NÃO INICIARAM'
      }
      if (now.isAfter(endDate)) {
        return 'CONFIRMAÇÕES ENCERRADAS'
      }
      return 'Confirmar presença'
    },
    confirmationDateRange() {
      const startDate = this.$moment(this.confirmationStartDate)
      const endDate = this.$moment(this.confirmationEndDate)
      const formattedStart = startDate.format('DD/MM/YYYY [às] HH:mm')
      const formattedEnd = endDate.format('DD/MM/YYYY [às] HH:mm')
      return `Confirmações liberadas de ${formattedStart} a ${formattedEnd}`
    },
    isFormDisabled() {
      return this.confirmed || !this.isConfirmationActive || this.isConfirmationLimitReached
    }
  },
  watch: {
    initialGuest: {
      handler(newVal) {
        if (newVal) {
          this.initializeGuestData()
        }
      },
      immediate: true,
      deep: true
    },
    initialCompanions: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.initializeCompanionsData()
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.initializeGuestData()
  },
  methods: {
    ...mapActions(['confirmConsumerStoreEventViaTemporaryToken']),
    ...mapMutations(['STORE_EVENT_CONFIRMED']),
    addDdiToPhone(value) {
      if (!value) return ''
      const cleaned = value.replace(/\D/g, '')
      // If phone already has DDI, return as is
      if (cleaned.length >= 12 && cleaned.startsWith('55')) {
        return `${cleaned}`
      }
      // Add DDI 55 if not present
      return `55${cleaned}`
    },
    formatCpf(value) {
      if (!value) return ''
      const cleaned = value.replace(/\D/g, '')
      if (cleaned.length === 11) {
        return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
      }
      return value
    },
    formatPhone(value) {
      if (!value) return ''
      const cleaned = value.replace(/\D/g, '')
      // Remove DDI (55) if present - strip first 2 digits if they are 55
      let phoneDigits = cleaned
      if (cleaned.length >= 12 && cleaned.startsWith('55')) {
        phoneDigits = cleaned.substring(2)
      }
      // Format without DDI
      if (phoneDigits.length === 10) {
        // (##) ####-#### (landline)
        return phoneDigits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
      } else if (phoneDigits.length === 11) {
        // (##) # ####-#### (mobile)
        return phoneDigits.replace(/(\d{2})(\d{1})(\d{4})(\d{4})/, '($1) $2 $3-$4')
      }
      return value
    },
    initializeGuestData() {
      if (this.initialGuest) {
        this.holderData.documentNo = this.formatCpf(this.initialGuest.documentNo || '')
        this.holderData.name = this.initialGuest.name || ''
        this.holderData.phone = this.formatPhone(this.initialGuest.phone || '')
      }
    },
    initializeCompanionsData() {
      if (this.initialCompanions && this.initialCompanions.length > 0) {
        this.companionsData = this.initialCompanions.map(companion => ({
          documentNo: this.formatCpf(companion.documentNo || ''),
          name: companion.name || '',
          phone: this.formatPhone(companion.phone || '')
        }))
      }
    },
    addCompanion() {
      if (!this.isMaxCompanionsReached) {
        this.companionsData.push({
          documentNo: '',
          name: '',
          phone: ''
        })
      }
    },
    removeCompanion(index) {
      this.companionsData.splice(index, 1)
    },
    validateHolder() {
      if (!this.$refs.holderCard) {
        return true
      }
      return this.$refs.holderCard.validate()
    },
    validateAllCompanions() {
      if (!this.$refs.companionCards || this.$refs.companionCards.length === 0) {
        return true
      }
      let allValid = true
      this.$refs.companionCards.forEach(card => {
        if (!card.validate()) {
          allValid = false
        }
      })
      return allValid
    },
    async handleConfirmClick() {
      if (this.isFormDisabled) return

      // Validate holder
      if (!this.validateHolder()) {
        this.$snackbar({
          message: 'Por favor, corrija os erros nos seus dados antes de confirmar.',
          snackbarColor: 'error'
        })
        return
      }

      // Validate all companions
      if (!this.validateAllCompanions()) {
        this.$snackbar({
          message: 'Por favor, corrija os erros nos dados dos acompanhantes antes de confirmar.',
          snackbarColor: 'error'
        })
        return
      }

      // Check if user has less companions than max allowed
      if (this.hasLessCompanionsThanMax) {
        const companionText = this.companionsData.length === 0 ? 'mas nenhum foi adicionado' : `mas só adicionou ${this.companionsData.length}`
        const confirmed = await this.$confirm({
          title: 'Confirmar sem todos os acompanhantes?',
          message: `Você pode adicionar até ${this.maxCompanions} acompanhante(s), ${companionText}. Após confirmar, não será possível adicionar mais acompanhantes.`,
          showCancelButton: true,
          confirmText: 'Confirmar',
          cancelText: 'Cancelar',
          confirmColor: this.confirmationSectionButtonBackgroundColor,
          confirmFontColor: this.confirmationSectionButtonFontColor,
          cancelColor: 'grey'
        })

        if (!confirmed) {
          return
        }
      }

      // If has terms, open terms modal; otherwise confirm directly
      await this.$nextTick()
      if (this.consumerTerms && this.$refs.termsModal) {
        this.$refs.termsModal.open()
      } else {
        this.confirmPresence()
      }
    },
    async confirmPresence() {
      // Don't call mutation if already confirmed or confirmation not active
      if (this.isFormDisabled) {
        return
      }

      try {
        this.loadingConfirm = true
        const token = this.GET_STORE_EVENT_TOKEN
        if (!token) {
          console.error('No temporary token available')
          return
        }

        // Format holder data with DDI
        const holderData = {
          name: this.holderData.name,
          phone: this.addDdiToPhone(this.holderData.phone)
        }

        // Format companions data with DDI
        const companionsData = this.companionsData.map(companion => ({
          documentNo: companion.documentNo.replace(/\D/g, ''),
          name: companion.name,
          phone: this.addDdiToPhone(companion.phone)
        }))

        const input = {
          temporaryAuthToken: token,
          holderData,
          companionsData
        }

        await this.confirmConsumerStoreEventViaTemporaryToken(input)

        // Set confirmed locally
        this.STORE_EVENT_CONFIRMED(true)
        this.$emit('confirmed')
      } catch (error) {
        console.error('Error confirming presence:', JSON.stringify(error))
        let errorMessage = 'Erro ao confirmar presença. Tente novamente.'
        // Parse GraphQL error response
        const graphqlError = error?.response?.errors?.[0]?.message
        if (graphqlError === 'Guest is already confirmed') {
          errorMessage = 'Convite já aceito por este convidado.'
        }

        this.$snackbar({
          message: errorMessage,
          snackbarColor: 'error'
        })
      } finally {
        this.loadingConfirm = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.confirmation-card {
  padding: 24px;
  max-width: 500px;
  margin: 0 auto;
  border-radius: 8px;
  text-align: center;
}

.confirmation-card__title {
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 12px;
  text-align: center;
}

.confirmation-card__description {
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 24px;
  text-align: center;
  opacity: 0.8;
}

.confirmation-card__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
}

.confirmation-card__add-btn {
  margin-top: 4px;
}

.confirmation-card__confirm-btn {
  margin-top: 8px;
  max-width: 400px;
}

.confirmation-card__date-range {
  font-size: 0.875rem;
  margin-top: 8px;
  text-align: center;
  opacity: 0.8;
}
</style>
